import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Loader2, Anchor, Mail, ArrowLeft, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminForgotPasswordPage = () => {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!email.trim()) {
      setError("Vul je e-mailadres in.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/admin/password-reset/request`, {
        email: email.trim().toLowerCase(),
      });
      setDone(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "Er ging iets mis. Probeer het later opnieuw.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="admin-forgot-page"
      className="min-h-screen bg-[#F4F8FB] flex items-center justify-center p-4"
      style={{
        backgroundImage:
          "radial-gradient(at 20% 0%, rgba(107,192,232,0.25), transparent 50%), radial-gradient(at 80% 100%, rgba(46,156,214,0.18), transparent 45%)",
      }}
    >
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center justify-center gap-1 mb-8 font-[Poppins] leading-none">
          <span className="text-3xl font-extrabold tracking-tight text-[#173A5E]">LEMMER</span>
          <span className="text-xl font-semibold text-[#2E9CD6] italic">Leeft</span>
        </Link>
        <div className="bg-white rounded-2xl shadow-xl border border-[#E4EEF5] p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-11 h-11 rounded-xl bg-[#173A5E] flex items-center justify-center">
              <Anchor className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-xs font-[Poppins] font-semibold uppercase tracking-wider text-[#5A7594]">
                Beheer
              </p>
              <h1 className="text-2xl font-bold text-[#173A5E] leading-tight">
                Wachtwoord vergeten
              </h1>
            </div>
          </div>

          {done ? (
            <div data-testid="admin-forgot-success" className="text-center py-4">
              <div className="w-14 h-14 rounded-full bg-[#E6F5EA] flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-7 h-7 text-[#2A7A3F]" />
              </div>
              <p className="text-[#173A5E] font-[Poppins] font-semibold mb-2">
                Check je e-mail
              </p>
              <p className="text-sm text-[#5A7594] font-[Inter] mb-6 leading-relaxed">
                Als <strong>{email}</strong> bekend is bij Lemmer Leeft Beheer,
                dan sturen we je binnen enkele minuten een reset-link. De link is <strong>15 minuten</strong> geldig.
              </p>
              <Link
                to="/beheer/login"
                className="inline-flex items-center gap-1.5 text-sm font-[Poppins] font-semibold text-[#2E9CD6] hover:text-[#173A5E]"
              >
                <ArrowLeft className="w-4 h-4" />
                Terug naar inloggen
              </Link>
            </div>
          ) : (
            <form onSubmit={onSubmit} noValidate className="space-y-4">
              <p className="text-sm text-[#5A7594] font-[Inter]">
                Vul het e-mailadres van je beheerdersaccount in. We sturen je een link om een nieuw wachtwoord in te stellen.
              </p>
              <div>
                <label htmlFor="email" className="block text-sm font-[Poppins] font-semibold text-[#173A5E] mb-1.5">
                  E-mailadres
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8AA3B8]" />
                  <input
                    id="email"
                    type="email"
                    data-testid="admin-forgot-email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="username"
                    placeholder="beheer@lemmer-leeft.nl"
                    className="w-full bg-white border border-[#E4EEF5] rounded-xl pl-9 pr-4 py-2.5 font-[Inter] text-[15px] text-[#173A5E] outline-none focus:border-[#2E9CD6] focus:ring-2 focus:ring-[#6BC0E8]/40 transition"
                  />
                </div>
              </div>

              {error && (
                <div className="p-3 rounded-xl bg-[#FDECEE] border border-[#F5C3C8] text-[#8b1420] text-sm font-[Inter]">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={submitting}
                data-testid="admin-forgot-submit"
                className="ll-btn-primary w-full disabled:opacity-60 disabled:cursor-wait"
              >
                {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                Stuur reset-link
              </button>

              <div className="text-center pt-1">
                <Link
                  to="/beheer/login"
                  className="inline-flex items-center gap-1.5 text-sm font-[Poppins] font-semibold text-[#2E9CD6] hover:text-[#173A5E]"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Terug naar inloggen
                </Link>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminForgotPasswordPage;
